// --- React & Redux
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
// --- External UI Libraries
import { Col, Row } from 'antd';
import Button from '@dtci/spellbook/Button';
import Input from '@dtci/spellbook/Input';
import Toggle from '@dtci/spellbook/Toggle';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel';
// --- Actions & Hooks
import { useDealVersionActions } from 'datastage/store/dealVersion/useDealVersion';
import { useDealVersionData } from '../../hooks/useDealVersionData';
// --- Utilities
import { getItemRange } from 'datastage/utils/pagination';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { cleanDealVersionFilter } from './utils/dealVersionSearchUtils';
import { validateSpecialCharacters } from 'datastage/utils/validateSpecialCharacters';
// --- Styles
import './styles.scss';

/**
 * DealVersion Search Form
 *
 * Features:
 * - Renders a filter form for Deal Versions (Deal ID, Deal Title, Show Snapshots)
 * - Debounced auto-search on input change (400ms delay)
 * - Input validation for Deal ID field
 * - Syncs filter state with URL params bidirectionally
 * - Reset button clears all filters
 *
 * @returns {JSX.Element}
 */
export function DealVersionSearchForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const isInitialMount = useRef(true);
    const debounceTimerRef = useRef(null);

    const { fetchDealVersions, updateDealVersion, rangeParam, sortParam, itemsPerPage } = useDealVersionData();
    const { updateDealVersionSearchForm, clearDealVersion } = useDealVersionActions();
    const { filterForm } = useSelector((s) => s.dealVersion);

    // Validation state for inline error messages
    const [validationErrors, setValidationErrors] = useState({
        dealId: undefined,
    });

    // Sync URL params to form state on navigation (location.key changes on each navigation)
    useEffect(() => {
        const params = parseSearchParams(location.search);
        const filterData = buildFilterFromParams(params);

        // Handle pageSize from URL (TAP pattern)
        let rangeOverride;
        if (params.pageSize) {
            const newPageSize = parseInt(params.pageSize, 10);
            if (!isNaN(newPageSize) && newPageSize > 0) {
                rangeOverride = getItemRange(0, newPageSize);
                updateDealVersion({
                    itemsPerPage: newPageSize,
                    rangeParam: rangeOverride,
                    currentPage: 0,
                });
            }
        }

        const hasUrlParams = Object.keys(filterData).length > 0;

        if (hasUrlParams) {
            updateDealVersionSearchForm({ key: 'filterForm', data: filterData });
        }

        // Merge URL-derived filters onto Redux filterForm so defaults apply when the URL
        // only carries partial params (e.g. pageSize).
        const filterForFetch = hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

        fetchData(filterForFetch, rangeOverride);
        isInitialMount.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    /**
     * Builds filter form data from URL search params.
     * Converts string params to appropriate types.
     */
    function buildFilterFromParams(params) {
        const filterData = {};

        if (params.dealId) filterData.dealId = params.dealId;
        if (params.dealTitle) filterData.dealTitle = params.dealTitle;

        // Convert showSubversions to boolean
        if ('showSubversions' in params) {
            filterData.showSubversions =
                params.showSubversions === 'true' || params.showSubversions === true;
        }

        return filterData;
    }

    /**
     * Fetches deal version data and updates Redux state.
     * @param {Object} filterData - Filter criteria
     * @param {Array} rangeOverride - Optional range override (e.g., when pageSize changes)
     */
    const fetchData = async (filterData, rangeOverride) => {
        // Merge with current filterForm to include all fields
        const mergedFilter = { ...filterForm, ...filterData };
        const range = rangeOverride ?? getItemRange(0, itemsPerPage);
        const data = await fetchDealVersions(range, mergedFilter);
        if (data) {
            updateDealVersion({
                dealVersions: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                totalItems: data?.totalElements ?? 0,
                rangeParam: range,
            });
        }
    };

    /**
     * Reset navigates to clean URL; the location.key useEffect handles re-fetch.
     */
    const handleResetFilter = () => {
        clearDealVersion();
        setValidationErrors({ dealId: undefined });
        navigate(location.pathname);
    };

    /**
     * Handles input changes with validation and debounced search.
     * Validates Deal ID field for special characters.
     * Triggers search after 400ms of no typing.
     */
    function handleInputChange(field, value) {
        let newValidationErrors = validationErrors;

        // Validate Deal ID field for special characters
        if (field === 'dealId') {
            const error = validateSpecialCharacters(value);
            newValidationErrors = { ...validationErrors, [field]: error };
            setValidationErrors(newValidationErrors);
        }

        // Update form state
        const newFilterForm = { ...filterForm, [field]: value };
        updateDealVersionSearchForm({
            key: 'filterForm',
            data: { [field]: value },
        });

        // Debounce the search to avoid excessive API calls
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            const hasErrors = Object.values(newValidationErrors).some((error) => error);
            if (!hasErrors) {
                const firstPageRange = getItemRange(0, itemsPerPage);
                updateDealVersion({ rangeParam: firstPageRange, currentPage: 0 });
                const filtered = cleanDealVersionFilter(newFilterForm, firstPageRange, sortParam);
                updateSearchParams(location, navigate, filtered);
            }
        }, 400);
    }

    return (
        <Row className="deal-version-filter-form">
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Deal ID">
                    <Input
                        value={filterForm.dealId}
                        onChange={(e) => handleInputChange('dealId', e.target.value)}
                        width="auto"
                        id="dealId"
                        name="dealId"
                        placeholder="Enter"
                        className={validationErrors.dealId ? 'input-error' : ''}
                    />
                    {validationErrors.dealId && (
                        <div className="validation-error">{validationErrors.dealId}</div>
                    )}
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Deal Title">
                    <Input
                        value={filterForm.dealTitle}
                        onChange={(e) => handleInputChange('dealTitle', e.target.value)}
                        width="auto"
                        id="dealTitle"
                        name="dealTitle"
                        placeholder="Enter"
                    />
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Show Snapshots">
                    <Toggle
                        checked={filterForm.showSubversions === true || filterForm.showSubversions === 'true'}
                        onChange={(e) => handleInputChange('showSubversions', e.target.checked)}
                        labelStyle={'yesNo'}
                    />
                </InputLabel>
            </Col>
            <Col>
                <Button
                    className="reset-button"
                    sizeType="small"
                    styleType="standard"
                    onClick={handleResetFilter}
                >
                    Reset
                </Button>
            </Col>
        </Row>
    );
}
